/**
 * Tahap 7A — Bank Reconciliation, Cash Control & Payment Matching.
 * Semua UI text Bahasa Indonesia.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatRupiah, formatDateID } from "@/lib/format";
import { Building2, Upload, GitCompare, ArrowRightLeft, Coins, ShieldCheck, LayoutDashboard, Landmark } from "lucide-react";
import { NoAccessBanner } from "../inventory/InventoryPages";

const BANK_PERMS = {
  super_admin: ["*"],
  keuangan: ["BANK_LIHAT", "BANK_IMPORT", "BANK_MATCH", "BANK_RECON_CREATE", "BANK_TRANSFER", "BANK_ADJUSTMENT"],
  akuntansi: ["BANK_LIHAT", "BANK_MATCH", "BANK_RECON_CREATE", "BANK_RECON_APPROVE", "BANK_TRANSFER", "BANK_ADJUSTMENT"],
  direktur_utama: ["BANK_LIHAT", "BANK_RECON_APPROVE", "BANK_RECON_LOCK"],
  direktur_operasional: ["BANK_LIHAT", "BANK_RECON_APPROVE"],
  auditor: ["BANK_LIHAT"], viewer: ["BANK_LIHAT"], perpajakan: ["BANK_LIHAT"], gudang: [],
};
function useBankCan() {
  const { user } = useAuth();
  const perms = BANK_PERMS[user?.role] || [];
  const isSuper = perms.includes("*");
  return (p) => isSuper || perms.includes(p);
}
function Crumb({ path }) { return <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">{path.map((p, i) => (<span key={i}>{p}{i < path.length - 1 && " › "}</span>))}</div>; }
function Stat({ label, value, tone = "slate", sub, testid }) {
  const map = { emerald: "bg-emerald-50 border-emerald-200", sky: "bg-sky-50 border-sky-200", amber: "bg-amber-50 border-amber-200", rose: "bg-rose-50 border-rose-200", slate: "bg-slate-50 border-slate-200", blue: "bg-blue-50 border-blue-200" };
  return <div className={`border rounded-md p-4 ${map[tone]}`} data-testid={testid}>
    <div className="text-xs opacity-80">{label}</div><div className="font-mono-num text-lg font-semibold mt-1">{value}</div>
    {sub && <div className="text-xs opacity-70 mt-1">{sub}</div>}</div>;
}
const statCls = (s) => ({
  SESUAI: "bg-emerald-600", PERLU_DITINJAU: "bg-amber-500", ERROR: "bg-rose-600",
  DRAFT: "bg-slate-500", MATCHING: "bg-blue-500", DIREVIEW: "bg-amber-500",
  DISETUJUI: "bg-emerald-600", DIKUNCI: "bg-slate-700", SELESAI: "bg-emerald-600",
  UNMATCHED: "bg-amber-500", MATCHED: "bg-emerald-600",
  TINGGI: "bg-emerald-600", SEDANG: "bg-amber-500", RENDAH: "bg-slate-500",
})[s] || "bg-slate-500";

/* ============ Bank Hub Dashboard ============ */
export function BankHub() {
  const can = useBankCan();
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!can("BANK_LIHAT")) return;
    api.get("/bank/dashboard").then(({ data }) => setD(data)).catch((e) => toast.error(formatApiError(e)));
  }, [can]);
  if (!can("BANK_LIHAT")) return <div data-testid="bank-hub-page"><NoAccessBanner perm="BANK_LIHAT" /></div>;
  return (
    <div className="space-y-6 page-fade" data-testid="bank-hub-page">
      <div><Crumb path={["Bank & Kas"]} /><h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Bank & Kas — Dasbor</h1></div>
      {d && <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total Saldo Bank" value={formatRupiah(d.total_bank_balance)} tone="emerald" testid="bank-stat-total" />
        <Stat label="Total Kas" value={formatRupiah(d.total_cash_balance)} tone="sky" testid="bank-stat-cash" />
        <Stat label="Statement Belum Match" value={d.unmatched_statement_lines} tone={d.unmatched_statement_lines > 0 ? "amber" : "slate"} testid="bank-stat-unmatched" />
        <Stat label="Outstanding AR / AP" value={`${formatRupiah(d.outstanding_ar)} / ${formatRupiah(d.outstanding_ap)}`} tone="blue" testid="bank-stat-arap" />
      </div>}
      {d && <Card><CardContent className="p-4">
        <h3 className="font-semibold mb-2">Saldo per Rekening</h3>
        <Table><TableHeader><TableRow><TableHead>Kode</TableHead><TableHead>Bank</TableHead><TableHead>COA</TableHead><TableHead>Saldo</TableHead></TableRow></TableHeader><TableBody>
          {(d.banks || []).map((b, i) => <TableRow key={i} data-testid={`bank-per-row-${i}`}><TableCell>{b.code}</TableCell><TableCell>{b.bank_name}</TableCell><TableCell>{b.coa_code}</TableCell><TableCell className="font-mono-num">{formatRupiah(b.balance)}</TableCell></TableRow>)}
        </TableBody></Table>
      </CardContent></Card>}
      {d && <Card><CardContent className="p-4">
        <h3 className="font-semibold mb-2">Cash Flow 30 Hari Terakhir</h3>
        <div className="grid grid-cols-2 gap-3">
          <Stat label="Cash Inflow" value={formatRupiah(d.cash_inflow_30d)} tone="emerald" testid="bank-inflow" />
          <Stat label="Cash Outflow" value={formatRupiah(d.cash_outflow_30d)} tone="rose" testid="bank-outflow" />
        </div>
      </CardContent></Card>}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[
          ["/bank/rekening", "Master Rekening", Landmark],
          ["/bank/import", "Import Statement", Upload],
          ["/bank/matching", "Payment Matching", GitCompare],
          ["/bank/transfer", "Transfer Bank", ArrowRightLeft],
          ["/bank/biaya", "Biaya & Bunga Bank", Coins],
          ["/bank/rekonsiliasi", "Rekonsiliasi", Building2],
          ["/bank/integritas", "Integritas Bank", ShieldCheck],
        ].map(([p, t, I]) => <Card key={p} className="hover:border-slate-400"><CardContent className="p-4">
          <div className="flex items-center gap-2"><I className="h-5 w-5 text-slate-700" /><div className="font-semibold">{t}</div></div>
          <Link to={p}><Button size="sm" variant="outline" className="mt-3" data-testid={`bank-nav-${p.split('/').pop()}`}>Buka</Button></Link>
        </CardContent></Card>)}
      </div>
    </div>
  );
}

/* ============ Master Rekening Bank ============ */
export function BankAccountsPage() {
  const can = useBankCan();
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!can("BANK_LIHAT")) return;
    api.get("/bank/accounts").then(({ data }) => setRows(data)).catch((e) => toast.error(formatApiError(e)));
  }, [can]);
  if (!can("BANK_LIHAT")) return <div data-testid="bank-accounts-page"><NoAccessBanner perm="BANK_LIHAT" /></div>;
  return <div className="space-y-6" data-testid="bank-accounts-page">
    <div><Crumb path={["Bank", "Master Rekening"]} /><h1 className="font-display text-3xl font-bold">Master Rekening Bank</h1></div>
    <Card><CardContent className="p-0"><Table>
      <TableHeader><TableRow><TableHead>Kode</TableHead><TableHead>Bank</TableHead><TableHead>Nomor Rekening</TableHead><TableHead>Nama</TableHead><TableHead>Currency</TableHead><TableHead>COA</TableHead><TableHead>Saldo Awal</TableHead></TableRow></TableHeader>
      <TableBody>
        {rows.map((r, i) => <TableRow key={i} data-testid={`bank-acc-row-${i}`}>
          <TableCell>{r.code}</TableCell><TableCell>{r.bank_name}</TableCell>
          <TableCell className="font-mono-num">{r.account_number}</TableCell>
          <TableCell>{r.account_holder}</TableCell>
          <TableCell>{r.currency}</TableCell><TableCell>{r.coa_code}</TableCell>
          <TableCell className="font-mono-num">{formatRupiah(r.opening_balance || 0)}</TableCell>
        </TableRow>)}
      </TableBody>
    </Table></CardContent></Card>
    <p className="text-xs text-slate-500">Nomor rekening ditampilkan bertopeng untuk peran read-only. Edit rekening via modul Master Data.</p>
  </div>;
}

/* ============ Import Bank Statement ============ */
export function StatementImportPage() {
  const can = useBankCan();
  const [banks, setBanks] = useState([]);
  const [form, setForm] = useState({ bank_account_code: "", period_start: "", period_end: "", csv_content: "" });
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (!can("BANK_IMPORT")) return;
    api.get("/bank/accounts").then(({ data }) => setBanks(data));
  }, [can]);
  if (!can("BANK_IMPORT")) return <div className="space-y-6" data-testid="bank-import-page"><h1 className="text-3xl font-bold">Import Bank Statement</h1><NoAccessBanner perm="BANK_IMPORT" /></div>;
  const doPreview = async () => {
    try { const { data } = await api.post("/bank/statements/preview", { ...form, format: "csv" }); setPreview(data); toast.success(`${data.total_lines} baris (${data.duplicate_count} duplikat)`); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const doCommit = async () => {
    try { const { data } = await api.post("/bank/statements/commit", { ...form, format: "csv" }); toast.success(`Import ${data.number}: ${data.saved_lines} disimpan, ${data.duplicate_skipped} duplikat dilewati`); setPreview(null); setForm({ ...form, csv_content: "" }); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return <div className="space-y-6" data-testid="bank-import-page">
    <div><Crumb path={["Bank", "Import Statement"]} /><h1 className="font-display text-3xl font-bold">Import Bank Statement (CSV)</h1><p className="text-sm text-slate-500 mt-1">Alur: UPLOAD → PREVIEW → VALIDATE (duplicate check) → COMMIT. Preview tidak menyentuh GL.</p></div>
    <Card><CardContent className="p-4 space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div><Label>Rekening</Label>
          <Select value={form.bank_account_code} onValueChange={(v) => setForm({ ...form, bank_account_code: v })}>
            <SelectTrigger data-testid="bank-import-account"><SelectValue placeholder="Pilih Rekening" /></SelectTrigger>
            <SelectContent>{banks.map(b => <SelectItem key={b.code} value={b.code}>{b.code} — {b.bank_name}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label>Periode Mulai</Label><Input type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} data-testid="bank-import-start" /></div>
        <div><Label>Periode Akhir</Label><Input type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} data-testid="bank-import-end" /></div>
      </div>
      <div><Label>CSV Content (kolom: date, description, reference, debit, credit, running_balance)</Label>
        <Textarea value={form.csv_content} onChange={(e) => setForm({ ...form, csv_content: e.target.value })} rows={8} className="font-mono-num text-xs" data-testid="bank-import-csv"
          placeholder="date,description,reference,debit,credit,running_balance
2026-02-01,Setoran Tunai,SET-001,0,5000000,5000000" /></div>
      <div className="flex gap-2">
        <Button variant="outline" onClick={doPreview} disabled={!form.bank_account_code || !form.csv_content} data-testid="bank-import-preview">Preview</Button>
        <Button onClick={doCommit} disabled={!preview} data-testid="bank-import-commit">Commit Import</Button>
      </div>
    </CardContent></Card>
    {preview && <Card><CardContent className="p-4">
      <h3 className="font-semibold mb-2">Preview — {preview.total_lines} baris ({preview.duplicate_count} duplikat)</h3>
      <Table><TableHeader><TableRow><TableHead>Tanggal</TableHead><TableHead>Deskripsi</TableHead><TableHead>Ref</TableHead><TableHead>Debit</TableHead><TableHead>Credit</TableHead><TableHead>Duplikat?</TableHead></TableRow></TableHeader><TableBody>
        {preview.lines.map((l, i) => <TableRow key={i} data-testid={`bank-preview-row-${i}`}>
          <TableCell>{l.date}</TableCell><TableCell>{l.description}</TableCell><TableCell>{l.reference}</TableCell>
          <TableCell className="font-mono-num">{formatRupiah(l.debit)}</TableCell>
          <TableCell className="font-mono-num">{formatRupiah(l.credit)}</TableCell>
          <TableCell>{l.duplicate ? <Badge className="bg-rose-600">DUPLIKAT</Badge> : <Badge className="bg-emerald-600">BARU</Badge>}</TableCell>
        </TableRow>)}
      </TableBody></Table>
    </CardContent></Card>}
  </div>;
}

/* ============ Payment Matching ============ */
export function MatchingPage() {
  const can = useBankCan();
  const [lines, setLines] = useState([]);
  const [selected, setSelected] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const load = async () => { try { const { data } = await api.get("/bank/statement-lines?status=UNMATCHED&limit=200"); setLines(data); } catch (e) { toast.error(formatApiError(e)); } };
  useEffect(() => { if (can("BANK_MATCH")) load(); }, [can]);
  if (!can("BANK_MATCH")) return <div className="space-y-6" data-testid="bank-matching-page"><h1 className="text-3xl font-bold">Payment Matching</h1><NoAccessBanner perm="BANK_MATCH" /></div>;
  const selectLine = async (sl) => {
    setSelected(sl); setSuggestions([]);
    try { const { data } = await api.get(`/bank/suggest-matches/${sl.id}`); setSuggestions(data.suggestions); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const confirmMatch = async (sug) => {
    try {
      await api.post("/bank/match", {
        statement_line_ids: [selected.id],
        internal_txn_refs: [{ key: sug.key, id: sug.id }],
        match_type: "FULL",
      });
      toast.success(`Match confirmed dengan ${sug.number}`);
      setSelected(null); setSuggestions([]); await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return <div className="space-y-6" data-testid="bank-matching-page">
    <div><Crumb path={["Bank", "Payment Matching"]} /><h1 className="font-display text-3xl font-bold">Payment Matching</h1><p className="text-sm text-slate-500 mt-1">Pilih baris rekening koran, sistem menampilkan saran match dengan confidence. User wajib konfirmasi.</p></div>
    <div className="grid md:grid-cols-2 gap-4">
      <Card><CardContent className="p-4">
        <h3 className="font-semibold mb-2">Bank Statement (Belum Match)</h3>
        <Table><TableHeader><TableRow><TableHead>Tanggal</TableHead><TableHead>Deskripsi</TableHead><TableHead>Debit/Kredit</TableHead><TableHead></TableHead></TableRow></TableHeader><TableBody>
          {lines.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-6 text-slate-400">Tidak ada baris tidak-match.</TableCell></TableRow>}
          {lines.map((l) => <TableRow key={l.id} onClick={() => selectLine(l)} className={`cursor-pointer ${selected?.id === l.id ? "bg-blue-50" : ""}`} data-testid={`bank-sl-row-${l.id}`}>
            <TableCell>{l.date}</TableCell><TableCell className="max-w-xs truncate">{l.description}</TableCell>
            <TableCell className="font-mono-num">{formatRupiah((l.credit || 0) - (l.debit || 0))}</TableCell>
            <TableCell><Button size="sm" variant="outline" data-testid={`bank-sl-pick-${l.id}`}>Pilih</Button></TableCell>
          </TableRow>)}
        </TableBody></Table>
      </CardContent></Card>
      <Card><CardContent className="p-4">
        <h3 className="font-semibold mb-2">Saran Match</h3>
        {!selected && <p className="text-slate-400 text-sm">Pilih baris rekening koran di kiri.</p>}
        {selected && suggestions.length === 0 && <p className="text-slate-400 text-sm">Tidak ada saran match untuk baris ini.</p>}
        {selected && suggestions.length > 0 && <Table><TableHeader><TableRow><TableHead>Tipe</TableHead><TableHead>Nomor</TableHead><TableHead>Amount</TableHead><TableHead>Confidence</TableHead><TableHead></TableHead></TableRow></TableHeader><TableBody>
          {suggestions.map((s) => <TableRow key={`${s.key}-${s.id}`} data-testid={`bank-sug-${s.id}`}>
            <TableCell>{s.key}</TableCell><TableCell>{s.number}</TableCell>
            <TableCell className="font-mono-num">{formatRupiah(s.amount)}</TableCell>
            <TableCell><Badge className={statCls(s.confidence)}>{s.confidence}</Badge></TableCell>
            <TableCell><Button size="sm" onClick={() => confirmMatch(s)} data-testid={`bank-sug-confirm-${s.id}`}>Cocokkan</Button></TableCell>
          </TableRow>)}
        </TableBody></Table>}
      </CardContent></Card>
    </div>
  </div>;
}

/* ============ Bank Transfer ============ */
export function BankTransferPage() {
  const can = useBankCan();
  const [banks, setBanks] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [form, setForm] = useState({ from_bank_code: "", to_bank_code: "", amount: 0, date: new Date().toISOString().slice(0, 10), reference: "", notes: "" });
  const load = async () => { try { const [b, t] = await Promise.all([api.get("/bank/accounts"), api.get("/bank/transfers")]); setBanks(b.data); setTransfers(t.data); } catch (e) { toast.error(formatApiError(e)); } };
  useEffect(() => { if (can("BANK_TRANSFER")) load(); }, [can]);
  if (!can("BANK_TRANSFER")) return <div className="space-y-6" data-testid="bank-transfer-page"><h1 className="text-3xl font-bold">Transfer Bank</h1><NoAccessBanner perm="BANK_TRANSFER" /></div>;
  const submit = async () => {
    try { await api.post("/bank/transfers", { ...form, amount: parseFloat(form.amount) }); toast.success("Transfer bank berhasil (paired OUT+IN)"); setForm({ ...form, amount: 0 }); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return <div className="space-y-6" data-testid="bank-transfer-page">
    <div><Crumb path={["Bank", "Transfer Bank"]} /><h1 className="font-display text-3xl font-bold">Transfer Antar Rekening</h1></div>
    <Card><CardContent className="p-4 space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div><Label>Dari</Label><Select value={form.from_bank_code} onValueChange={(v) => setForm({ ...form, from_bank_code: v })}><SelectTrigger data-testid="bt-from"><SelectValue placeholder="Rekening asal" /></SelectTrigger><SelectContent>{banks.map(b => <SelectItem key={b.code} value={b.code}>{b.bank_name}</SelectItem>)}</SelectContent></Select></div>
        <div><Label>Ke</Label><Select value={form.to_bank_code} onValueChange={(v) => setForm({ ...form, to_bank_code: v })}><SelectTrigger data-testid="bt-to"><SelectValue placeholder="Rekening tujuan" /></SelectTrigger><SelectContent>{banks.map(b => <SelectItem key={b.code} value={b.code}>{b.bank_name}</SelectItem>)}</SelectContent></Select></div>
        <div><Label>Nominal</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="bt-amount" /></div>
        <div><Label>Tanggal</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="bt-date" /></div>
        <div className="col-span-2"><Label>Reference</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="bt-ref" /></div>
        <div className="col-span-2"><Label>Catatan</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="bt-notes" /></div>
      </div>
      <Button onClick={submit} data-testid="bt-submit"><ArrowRightLeft className="h-4 w-4 mr-2" />Buat Transfer</Button>
    </CardContent></Card>
    <Card><CardContent className="p-0"><Table>
      <TableHeader><TableRow><TableHead>Nomor</TableHead><TableHead>Tanggal</TableHead><TableHead>Dari</TableHead><TableHead>Ke</TableHead><TableHead>Nominal</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
      <TableBody>
        {transfers.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-400">TIDAK ADA DATA</TableCell></TableRow>}
        {transfers.map((t) => <TableRow key={t.id} data-testid={`bt-row-${t.id}`}>
          <TableCell>{t.number}</TableCell><TableCell>{t.date}</TableCell>
          <TableCell>{t.from_bank_name}</TableCell><TableCell>{t.to_bank_name}</TableCell>
          <TableCell className="font-mono-num">{formatRupiah(t.amount)}</TableCell>
          <TableCell><Badge className={statCls(t.status)}>{t.status}</Badge></TableCell>
        </TableRow>)}
      </TableBody>
    </Table></CardContent></Card>
  </div>;
}

/* ============ Bank Charges ============ */
export function BankChargesPage() {
  const can = useBankCan();
  const [banks, setBanks] = useState([]);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ bank_account_code: "", charge_type: "ADMIN", amount: 0, date: new Date().toISOString().slice(0, 10), description: "" });
  const load = async () => { const [b, c] = await Promise.all([api.get("/bank/accounts"), api.get("/bank/charges")]); setBanks(b.data); setRows(c.data); };
  useEffect(() => { if (can("BANK_ADJUSTMENT")) load().catch((e) => toast.error(formatApiError(e))); }, [can]);
  if (!can("BANK_ADJUSTMENT")) return <div className="space-y-6" data-testid="bank-charges-page"><h1 className="text-3xl font-bold">Biaya & Bunga Bank</h1><NoAccessBanner perm="BANK_ADJUSTMENT" /></div>;
  const submit = async () => {
    try { await api.post("/bank/charges", { ...form, amount: parseFloat(form.amount) }); toast.success("Biaya/bunga bank terposting"); setForm({ ...form, amount: 0 }); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return <div className="space-y-6" data-testid="bank-charges-page">
    <div><Crumb path={["Bank", "Biaya & Bunga"]} /><h1 className="font-display text-3xl font-bold">Biaya Admin, Transfer Fee & Bunga Bank</h1></div>
    <Card><CardContent className="p-4 space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div><Label>Rekening</Label><Select value={form.bank_account_code} onValueChange={(v) => setForm({ ...form, bank_account_code: v })}><SelectTrigger data-testid="bch-bank"><SelectValue placeholder="Pilih" /></SelectTrigger><SelectContent>{banks.map(b => <SelectItem key={b.code} value={b.code}>{b.bank_name}</SelectItem>)}</SelectContent></Select></div>
        <div><Label>Tipe</Label><Select value={form.charge_type} onValueChange={(v) => setForm({ ...form, charge_type: v })}><SelectTrigger data-testid="bch-type"><SelectValue /></SelectTrigger><SelectContent>
          <SelectItem value="ADMIN">Biaya Admin</SelectItem><SelectItem value="TRANSFER_FEE">Biaya Transfer</SelectItem>
          <SelectItem value="INTEREST_EXPENSE">Bunga Bank (Beban)</SelectItem><SelectItem value="INTEREST_INCOME">Bunga Bank (Pendapatan Jasa Giro)</SelectItem>
        </SelectContent></Select></div>
        <div><Label>Nominal</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="bch-amount" /></div>
        <div><Label>Tanggal</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="bch-date" /></div>
      </div>
      <div><Label>Deskripsi</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="bch-desc" /></div>
      <Button onClick={submit} data-testid="bch-submit">Simpan</Button>
    </CardContent></Card>
    <Card><CardContent className="p-0"><Table>
      <TableHeader><TableRow><TableHead>Nomor</TableHead><TableHead>Tanggal</TableHead><TableHead>Tipe</TableHead><TableHead>Nominal</TableHead><TableHead>Deskripsi</TableHead></TableRow></TableHeader>
      <TableBody>{rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-6 text-slate-400">TIDAK ADA DATA</TableCell></TableRow>}
        {rows.map((r) => <TableRow key={r.id}><TableCell>{r.number}</TableCell><TableCell>{r.date}</TableCell><TableCell>{r.charge_type}</TableCell><TableCell className="font-mono-num">{formatRupiah(r.amount)}</TableCell><TableCell>{r.description}</TableCell></TableRow>)}
      </TableBody>
    </Table></CardContent></Card>
  </div>;
}

/* ============ Reconciliation FSM ============ */
export function ReconciliationPage() {
  const can = useBankCan();
  const [banks, setBanks] = useState([]);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ bank_account_code: "", period_start: "", period_end: "", statement_closing_balance: 0, notes: "" });
  const load = async () => { const [b, r] = await Promise.all([api.get("/bank/accounts"), api.get("/bank/reconciliations")]); setBanks(b.data); setRows(r.data); };
  useEffect(() => { if (can("BANK_LIHAT")) load().catch((e) => toast.error(formatApiError(e))); }, [can]);
  if (!can("BANK_LIHAT")) return <div data-testid="bank-recon-page"><NoAccessBanner perm="BANK_LIHAT" /></div>;
  const create = async () => {
    try { await api.post("/bank/reconciliations", { ...form, statement_closing_balance: parseFloat(form.statement_closing_balance) }); toast.success("Rekonsiliasi dibuat (DRAFT)"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const transition = async (rid, action) => {
    try { await api.post(`/bank/reconciliations/${rid}/${action}`); toast.success(`Rekonsiliasi ${action}`); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const canCreate = can("BANK_RECON_CREATE"); const canApprove = can("BANK_RECON_APPROVE"); const canLock = can("BANK_RECON_LOCK");
  return <div className="space-y-6" data-testid="bank-recon-page">
    <div><Crumb path={["Bank", "Rekonsiliasi"]} /><h1 className="font-display text-3xl font-bold">Bank Reconciliation</h1><p className="text-sm text-slate-500 mt-1">FSM: DRAFT → MATCHING → DIREVIEW → DISETUJUI → DIKUNCI. Setelah DIKUNCI wajib pakai reversal resmi.</p></div>
    {canCreate && <Card><CardContent className="p-4 space-y-3">
      <h3 className="font-semibold">Buat Rekonsiliasi</h3>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <div><Label>Rekening</Label><Select value={form.bank_account_code} onValueChange={(v) => setForm({ ...form, bank_account_code: v })}><SelectTrigger data-testid="rec-bank"><SelectValue placeholder="Pilih" /></SelectTrigger><SelectContent>{banks.map(b => <SelectItem key={b.code} value={b.code}>{b.bank_name}</SelectItem>)}</SelectContent></Select></div>
        <div><Label>Mulai</Label><Input type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} data-testid="rec-start" /></div>
        <div><Label>Akhir</Label><Input type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} data-testid="rec-end" /></div>
        <div><Label>Saldo Akhir Menurut Bank</Label><Input type="number" value={form.statement_closing_balance} onChange={(e) => setForm({ ...form, statement_closing_balance: e.target.value })} data-testid="rec-closing" /></div>
        <div><Label>Catatan (bila selisih)</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="rec-notes" /></div>
      </div>
      <Button onClick={create} data-testid="rec-create">Buat DRAFT</Button>
    </CardContent></Card>}
    <Card><CardContent className="p-0"><Table>
      <TableHeader><TableRow><TableHead>Nomor</TableHead><TableHead>Rekening</TableHead><TableHead>Periode</TableHead><TableHead>Saldo Buku</TableHead><TableHead>Saldo Bank</TableHead><TableHead>Selisih</TableHead><TableHead>Status</TableHead><TableHead>Aksi</TableHead></TableRow></TableHeader>
      <TableBody>
        {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-6 text-slate-400">TIDAK ADA DATA</TableCell></TableRow>}
        {rows.map((r) => <TableRow key={r.id} data-testid={`rec-row-${r.id}`}>
          <TableCell>{r.number}</TableCell><TableCell>{r.bank_account_code}</TableCell>
          <TableCell className="text-xs">{r.period_start} → {r.period_end}</TableCell>
          <TableCell className="font-mono-num">{formatRupiah(r.book_balance)}</TableCell>
          <TableCell className="font-mono-num">{formatRupiah(r.statement_closing_balance)}</TableCell>
          <TableCell className="font-mono-num">{formatRupiah(r.difference)}</TableCell>
          <TableCell><Badge className={statCls(r.status)}>{r.status}</Badge></TableCell>
          <TableCell className="space-x-1">
            {canCreate && r.status === "DRAFT" && <Button size="sm" onClick={() => transition(r.id, "matching")} data-testid={`rec-match-${r.id}`}>Matching</Button>}
            {canCreate && r.status === "MATCHING" && <Button size="sm" onClick={() => transition(r.id, "review")} data-testid={`rec-review-${r.id}`}>Review</Button>}
            {canApprove && r.status === "DIREVIEW" && <Button size="sm" onClick={() => transition(r.id, "approve")} data-testid={`rec-approve-${r.id}`}>Setujui</Button>}
            {canLock && r.status === "DISETUJUI" && <Button size="sm" onClick={() => transition(r.id, "lock")} data-testid={`rec-lock-${r.id}`}>Kunci</Button>}
          </TableCell>
        </TableRow>)}
      </TableBody>
    </Table></CardContent></Card>
  </div>;
}

/* ============ Bank Integrity ============ */
export function BankIntegrityPage() {
  const can = useBankCan();
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!can("BANK_LIHAT")) return;
    api.get("/bank/integrity").then(({ data }) => setD(data)).catch((e) => toast.error(formatApiError(e)));
  }, [can]);
  if (!can("BANK_LIHAT")) return <div data-testid="bank-integrity-page"><NoAccessBanner perm="BANK_LIHAT" /></div>;
  return <div className="space-y-6" data-testid="bank-integrity-page">
    <div><Crumb path={["Bank", "Integritas"]} /><div className="flex items-center justify-between flex-wrap gap-3">
      <h1 className="font-display text-3xl font-bold">Bank Integrity Center</h1>
      {d && <Badge className={statCls(d.overall)} data-testid="bank-int-overall">{d.overall}</Badge>}
    </div></div>
    <Card><CardContent className="p-4"><Table>
      <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Cek</TableHead><TableHead>Ekspektasi</TableHead><TableHead>Aktual</TableHead><TableHead>Selisih/Mismatch</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
      <TableBody>{(d?.checks || []).map((c, i) => <TableRow key={i} data-testid={`bank-int-row-${i}`}>
        <TableCell>{i + 1}</TableCell><TableCell>{c.name}</TableCell>
        <TableCell className="font-mono-num">{c.expected !== undefined ? c.expected : "-"}</TableCell>
        <TableCell className="font-mono-num">{c.actual !== undefined ? c.actual : "-"}</TableCell>
        <TableCell className="font-mono-num">{c.mismatch_count !== undefined ? `${c.mismatch_count}×` : (c.difference !== undefined ? c.difference : "-")}</TableCell>
        <TableCell><Badge className={statCls(c.status)}>{c.status}</Badge></TableCell>
      </TableRow>)}</TableBody>
    </Table></CardContent></Card>
  </div>;
}
